import type { CrmChildRow } from '@/Containers/CrmSection/Components/ChildList'
import type { UserLike } from '@/Ship/Helpers/fieldViews'

/** Власник пропозиції: запит, тендер чи заявка (`SPEC-024` §3.3). */
export type TransportOfferKind = 'request' | 'tender' | 'application'

/** Звідки береться пропозиція кожного власника — ключ ресурсу, колекція, файли. */
export interface TransportOfferKindConfig {
    /** Ключ фронт-ресурсу: `Transportation.RequestOffer` */
    resource: string
    /** Колекція: `/services/request_offers` */
    restUrl: string
    /** Поле власника в пропозиції — і префільтр колекції (`request.uuid`) */
    ownerField: TransportOfferKind
    /** Крок 1 двокрокового завантаження; у пропозиції заявки файлів немає */
    fileUploadUrl: string | null
    /** Чи є колонка й поле «Перевізник» (лише заявка, `SPEC-024` §4.2) */
    hasCarrier: boolean
}

export const TRANSPORT_OFFER_KINDS: Record<TransportOfferKind, TransportOfferKindConfig> = {
    request: {
        resource: 'Transportation.RequestOffer',
        restUrl: '/services/request_offers',
        ownerField: 'request',
        fileUploadUrl: '/services/request_offer_files',
        hasCarrier: false,
    },
    tender: {
        resource: 'Transportation.TenderOffer',
        restUrl: '/services/tender_offers',
        ownerField: 'tender',
        fileUploadUrl: '/services/tender_offer_files',
        hasCarrier: false,
    },
    application: {
        resource: 'Transportation.ApplicationOffer',
        restUrl: '/services/application_offers',
        ownerField: 'application',
        fileUploadUrl: null,
        hasCarrier: true,
    },
}

/** Найдовший коментар пропозиції (`AbstractTransportOfferEntity`, Length ≤ 4096). */
export const TRANSPORT_OFFER_COMMENT_MAX_LENGTH = 4096

/** Валюта пропозиції за замовчуванням (ПР-04) — та сама, що ставить бек. */
export const TRANSPORT_OFFER_DEFAULT_CURRENCY = 'EUR'

/**
 * Колекційне право «обирати автора пропозиції» (`GET /services/auth/permissions`,
 * ключі `RequestOffer`, `TenderOffer`, `ApplicationOffer`): «лише логіст» його
 * не має й може вказати автором лише себе (ПР-02).
 */
export const TRANSPORT_OFFER_CHOOSE_RESPONSIBLE = 'chooseResponsible'

interface NamedRef {
    '@id'?: string
    uuid?: string
    title?: string
    code?: string
}

/** Рядок пропозиції так, як його віддає колекція (лише те, що читає блок). */
export interface TransportOfferRow extends CrmChildRow {
    offeredPrice?: number | null
    offeredPriceCurrency?: NamedRef | null
    comment?: string | null
    responsible?: UserLike | null
    carrier?: NamedRef | null
    files?: Array<{ uuid?: string }> | null
    createdAt?: string | null
    updatedAt?: string | null
}
